import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { Subscription, firstValueFrom, of } from "rxjs";
import { ModalService } from "../../services/modal/modal.service";
import { DataService } from "../../services/api/data/data.service";
import { Crop } from "../../models/crop.model";
import { ReactiveFormsModule } from "@angular/forms";
import moment from "moment";
import { DropdownComponent } from "../../dashboard/utils/dropdown/dropdown.component";
import { InfoPopoverComponent } from "../../dashboard/utils/info-popover/info-popover.component";
import { AuthService } from "../../services/api/auth/auth.service";
import {
  startDateWaterYear,
  endDateWaterYear,
} from "../../field-claim/map/util/utliWaterYear";
import { LoadingService } from "../../services/loading/loading";
import { SnackbarService } from "../../services/snackbar/snackbar.service";
import { deleteFieldUtil } from "../../utils/field-operations.util";
import { GraphPopoverInstructionComponent } from "../../dashboard/utils/graph-popover-instruction/graph-popover-instruction.component";
import { FieldEntity, FieldFormData } from "../../models/field-form.model";

@Component({
  selector: "app-field-selection-update",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownComponent,
    InfoPopoverComponent,
    GraphPopoverInstructionComponent,
  ],
  templateUrl: "./field-selection-update.component.html",
  styleUrl: "./field-selection-update.component.scss",
})
export class FieldSelectionUpdateComponent implements OnInit, OnDestroy {
  visible = false;
  private modalSubscription: Subscription | undefined;
  private isInitialized = false;
  private elementRef = inject(ElementRef);
  authService = inject(AuthService);
  // Tracks the previous Claim-As selection so we can revert if the user
  // cancels the "switch grower will discard your entities" confirmation.
  private lastClaimAsUserId: string;
  // True while we're programmatically reverting the dropdown — guards the
  // effect from re-prompting on the revert itself.
  private isRevertingClaimAs = false;
  // True while the "Discard selected entities?" confirm dialog is open.
  // Bound to the Claim As dropdown's [disabled] so the manager can't
  // start a second switch while the first one is still being confirmed.
  isAwaitingClaimAsConfirm = false;

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    if (
      this.showEntityDropdown &&
      !this.elementRef.nativeElement.contains(event.target)
    ) {
      this.showEntityDropdown = false;
    }
  }
  @Input() id!: number;
  @Input() client_name?: string;
  @Input() owner?: boolean;
  @Input() crops?: Crop[];
  @Input() cropsNoFallow?: Crop[];
  @Input() isManager?: boolean = false;
  @Input() properties?: any;
  @Output() fieldOperatedEvent = new EventEmitter();
  isFormValid = false;
  // Define a cropPairs that stores cropName: legend crop Name
  private cropPairs: { [key: string]: string };
  private cropIsPerennial: { [key: string]: boolean };
  validationInfo: string = "";
  readonly irrigationDropdownLabel = "Primary Irrigation Method";
  isFallow: boolean = false;
  private readonly claimDisclaimerKeyPrefix = "claimDisclaimerAcknowledged:";
  private preclaimedRequestSeq = 0;
  hasPreclaimedInfoForClaim = false;
  preclaimedInfoAcknowledged = false;

  // Entity multi-select state
  growerEntities: FieldEntity[] = [];
  selectedEntities: FieldEntity[] = [];
  showEntityDropdown = false;
  showAddNewEntityForm = false;
  newEntityName = "";
  newEntityEmail = "";
  newEntityValidationMsg = "";
  // True while the backend pre-flight check for the new entity email is
  // in flight. Bound to the Save button's [disabled] so users can't
  // double-submit while we're verifying.
  isCheckingEntityEmail = false;

  // Claim As (manager only) state
  private decode = this.authService.getDecodedToken();
  currentFirstLastName =
    [this.decode["first_name"], this.decode["last_name"]]
      .filter(Boolean)
      .join(" ")
      .trim() || this.decode["email"];
  selectedClaimAsUserId = signal<string>(this.authService.getUserId());

  /**
   * Whether the entity multi-select should be visible.
   * - Growers: always visible.
   * - Managers: only visible when they're claiming on behalf of a grower
   *   (i.e. they picked someone other than themselves in "Claim As").
   */
  showEntitySection = computed<boolean>(() => {
    if (!this.isManager) return true;
    return this.selectedClaimAsUserId() !== this.authService.getUserId();
  });

  // Signal-driven first-crop irrigation method
  readonly irrigationMethodsOptions = [
    { key: "Drip", value: "Drip" },
    {
      key: "Microsprinkler/Microsprayer",
      value: "Microsprinkler/Microsprayer",
    },
    { key: "Sprinkler", value: "Sprinkler" },
    { key: "Furrow", value: "Furrow" },
    { key: "Border Strip", value: "Border Strip" },
    { key: "Basin", value: "Basin" },
    { key: "Flood", value: "Flood" },
    { key: "Center Pivot", value: "Center Pivot" },
    { key: "Linear Move", value: "Linear Move" },
    { key: "Non-irrigated", value: "Non-irrigated" },
  ];
  firstCropIrrigationMethodSelectedOption = signal<string>("Drip");

  // Water-year bounds for the crop date pickers. Computed once at construction
  // so the modal can't render with stale literals; same source as the
  // ls_start_01 / ls_end_01 defaults set in updateFormValue().
  readonly waterYearMinDate = startDateWaterYear().format("YYYY-MM-DD");
  readonly waterYearMaxDate = endDateWaterYear().format("YYYY-MM-DD");

  /**
   * Form Data Binding and Validation
   */
  disableInput(event: KeyboardEvent): void {
    event.preventDefault(); // Prevent manual input
  }

  // Function to close the Modal
  closeModal(): void {
    this.modalService.toggleFieldSelectionUpdateModal();
    this.isFormValid = false;
    this.validationInfo = "";
  }

  sleep(ms: number | undefined) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  pollData(interval: number, maxAttempts: number): Promise<any> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const polling = setInterval(async () => {
        if (attempts >= maxAttempts) {
          clearInterval(polling);
          //
          reject(new Error("Max attempts reached"));
          return;
        }
        try {
          const response = await firstValueFrom(
            this.dataService.getFieldById(this.id.toString()),
          );
          // Add a response check here to determine success
          if (response) {
            // Adjust 'response.success' to your actual response structure
            clearInterval(polling); // Stop polling when the condition is met
            resolve(response); // Resolve the promise with the response
          } else {
          }
        } catch (error) {
          console.error("Error fetching data:", error);
          clearInterval(polling);
          reject(error); // Reject the promise if an error occurs
        }
        attempts++;
      }, interval);
    });
  }

  private hasReadyEtValues(field: any): boolean {
    const etValues = field?.userFieldEtvalues;
    if (Array.isArray(etValues)) {
      return etValues.length > 0;
    }
    return etValues && Object.keys(etValues).length > 0;
  }

  private async pollClaimedFieldEtReady(
    interval: number,
    maxAttempts: number,
  ): Promise<any> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const claimedFields = await firstValueFrom(
        this.isManager ?
          this.dataService.getClaimedFieldsManagerWithEt()
        : this.dataService.getClaimedFieldsWithEt(this.authService.getUserId()),
      );
      const claimedField = (claimedFields ?? []).find(
        (field: any) => Number(field.field_id) === Number(this.id),
      );
      if (claimedField && this.hasReadyEtValues(claimedField)) {
        return claimedField;
      }
      await this.sleep(interval);
    }
    throw new Error("ET values were not ready in time");
  }

  growersListData$ = rxResource({
    stream: () => (this.isManager ? this.dataService.getGrowerList() : of([])),
  });

  claimAsOptionList = computed<Array<{ key: string; label: string }>>(() => {
    let ret: Array<{ key: string; label: string }> = [];
    if (!this.isManager) {
      return ret;
    }

    const currentUserId = this.authService.getUserId();
    ret = [{ key: currentUserId, label: this.currentFirstLastName + " (You)" }];

    const growerList = this.growersListData$.value();
    if (!growerList) {
      // rxResource is undefined while the grower list is still loading.
      return ret;
    }
    growerList.forEach((grower) => {
      if (grower.u_id === currentUserId) {
        return;
      }
      const name =
        grower.u_first_name && grower.u_last_name ?
          `${grower.u_first_name} ${grower.u_last_name} (${grower.u_email})`
        : grower.u_email;
      ret.push({
        key: grower.u_id,
        label: name,
      });
    });
    return ret;
  });

  // Define the constructor, including dataService and modalService
  constructor(
    private dataService: DataService,
    private modalService: ModalService,
    private loadingService: LoadingService,
    private snackbarService: SnackbarService,
  ) {
    // Signal is the source of truth for the first crop irrigation method.
    // Push every change through updateFormValue so formData stays in sync.
    effect(() => {
      const value = this.firstCropIrrigationMethodSelectedOption();
      this.updateFormValue("irrigation_methods_01", { target: { value } });
    });

    this.lastClaimAsUserId = this.authService.getUserId();

    // React to manager switching the "Claim As" target. When they pick a
    // different grower, we need to reload that grower's entity catalog and
    // (if entities are already selected) confirm before discarding them.
    effect(() => {
      const newId = this.selectedClaimAsUserId();
      if (this.isRevertingClaimAs) {
        this.isRevertingClaimAs = false;
        this.lastClaimAsUserId = newId;
        return;
      }
      if (!this.isManager) return;
      if (newId === this.lastClaimAsUserId) return;
      this.handleClaimAsChange(newId, this.lastClaimAsUserId);
      this.lastClaimAsUserId = newId;
    });

    // Assign values to cropPairs
    this.cropPairs = {
      Alfalfa: "Alfalfa and Alfalfa Mixtures",
      "Almonds - with cover crop": "Almonds",
      "Almonds - without cover crop": "Almonds",
      "Apples - with cover crop": "Apples",
      "Apples - without cover crop": "Apples",
      "Apricots - with cover crop": "Apricots",
      "Apricots - without cover crop": "Apricots",
      "Beans (dry)": "Beans (Dry)",
      "Garbanzo Beans": "Beans (Dry)",
      Blueberries: "Bush Berries",
      Carrots: "Carrots",
      "Cherries - with cover crop": "Cherries",
      "Cherries - without cover crop": "Cherries",
      "Grapefruit - with cover crop": "Citrus",
      "Grapefruit - without cover crop": "Citrus",
      "Lemon - with cover crop": "Citrus",
      "Lemon - without cover crop": "Citrus",
      "Mandarin - with cover crop": "Citrus",
      "Mandarin - without cover crop": "Citrus",
      "Orange (navel) - with cover crop": "Citrus",
      "Orange (navel) - without cover crop": "Citrus",
      "Orange (valencia) - with cover crop": "Citrus",
      "Orange (valencia) - without cover crop": "Citrus",
      "Other Citrus - with cover crop": "Citrus",
      "Other Citrus - without cover crop": "Citrus",
      "Tangerines - with cover crop": "Citrus",
      "Tangerines - without cover crop": "Citrus",
      "Cole Crops (Broccoli/Cauliflower)": "Cole Crops",
      "Corn (field)": "Corn, Sorghum and Sudan",
      "Corn (sweet)": "Corn, Sorghum and Sudan",
      Sorghum: "Corn, Sorghum and Sudan",
      Sudangrass: "Corn, Sorghum and Sudan",
      Cotton: "Cotton",
      "Grapes (raisin) - with cover crop": "Grapes",
      "Grapes (raisin) - without cover crop": "Grapes",
      "Grapes (table) - with cover crop": "Grapes",
      "Grapes (table) - without cover crop": "Grapes",
      "Grapes (wine) - with cover crop": "Grapes",
      "Grapes (wine) - without cover crop": "Grapes",
      "Kiwi - with cover crop": "Kiwi",
      "Kiwi - without cover crop": "Kiwi",
      Lettuce: "Lettuce/Leafy Greens",
      Cantaloupe: "Melons, Squash and Cucumbers",
      Cucumbers: "Melons, Squash and Cucumbers",
      "Honeydew melons": "Melons, Squash and Cucumbers",
      Pumpkins: "Melons, Squash and Cucumbers",
      Squash: "Melons, Squash and Cucumbers",
      Watermelon: "Melons, Squash and Cucumbers",
      "Figs - with cover crop": "Miscellaneous Deciduous",
      "Figs - without cover crop": "Miscellaneous Deciduous",
      "Miscellaneous Deciduous - with cover crop": "Miscellaneous Deciduous",
      "Miscellaneous Deciduous - without cover crop": "Miscellaneous Deciduous",
      "Persimmons - with cover crop": "Miscellaneous Deciduous",
      "Persimmons - without cover crop": "Miscellaneous Deciduous",
      Hemp: "Miscellaneous Field Crops",
      Barley: "Miscellaneous Grain and Hay",
      "Cover Crop": "Miscellaneous Grain and Hay",
      "Forage Mix": "Miscellaneous Grain and Hay",
      "Other Small Grains": "Miscellaneous Grain and Hay",
      Teff: "Miscellaneous Grain and Hay",
      Triticale: "Miscellaneous Grain and Hay",
      Ryegrass: "Miscellaneous Grasses",
      "Miscellaneous Subtropical - with cover crop":
        "Miscellaneous Subtropical Fruits",
      "Miscellaneous Subtropical - without cover crop":
        "Miscellaneous Subtropical Fruits",
      "Miscellaneous Truck Crops": "Miscellaneous Truck Crops",
      Pasture: "Mixed Pasture",
      "Olives - with cover crop": "Olives",
      "Olives - without cover crop": "Olives",
      Garlic: "Onions and Garlic",
      Onions: "Onions and Garlic",
      "Nectarines - with cover crop": "Peaches/Nectarines",
      "Nectarines - without cover crop": "Peaches/Nectarines",
      "Peaches - with cover crop": "Peaches/Nectarines",
      "Peaches - without cover crop": "Peaches/Nectarines",
      "Pears - with cover crop": "Pears",
      "Pears - without cover crop": "Pears",
      "Pecans - with cover crop": "Pecans",
      "Pecans - without cover crop": "Pecans",
      Peppers: "Peppers",
      "Pistachios - with cover crop": "Pistachios",
      "Pistachios - without cover crop": "Pistachios",
      "Plums - with cover crop": "Plums",
      "Plums - without cover crop": "Plums",
      "Pomegranates - with cover crop": "Pomegranates",
      "Pomegranates - without cover crop": "Pomegranates",
      Potatoes: "Potatoes",
      "Prunes - with cover crop": "Prunes",
      "Prunes - without cover crop": "Prunes",
      Rice: "Rice",
      Safflower: "Safflower",
      Strawberries: "Strawberries",
      Sunflower: "Sunflower",
      "Sweet Potatoes": "Sweet Potatoes",
      Tomatoes: "Tomatoes",
      Turf: "Turf",
      Fallow: "Unclassified Fallow",
      "Walnuts - with cover crop": "Walnuts",
      "Walnuts - without cover crop": "Walnuts",
      Wheat: "Wheat",
      "Sugar Beets": "Sugar Beets",
      Oats: "Miscellaneous Grain and Hay",
    };
    // Assign values to cropIsPerennial
    this.cropIsPerennial = {
      Alfalfa: true,
      "Almonds - with cover crop": true,
      "Almonds - without cover crop": true,
      "Apples - with cover crop": true,
      "Apples - without cover crop": true,
      "Apricots - with cover crop": true,
      "Apricots - without cover crop": true,
      "Beans (dry)": false,
      "Garbanzo Beans": false,
      Blueberries: true,
      Carrots: false,
      "Cherries - with cover crop": true,
      "Cherries - without cover crop": true,
      "Grapefruit - with cover crop": true,
      "Grapefruit - without cover crop": true,
      "Lemon - with cover crop": true,
      "Lemon - without cover crop": true,
      "Mandarin - with cover crop": true,
      "Mandarin - without cover crop": true,
      "Orange (navel) - with cover crop": true,
      "Orange (navel) - without cover crop": true,
      "Orange (valencia) - with cover crop": true,
      "Orange (valencia) - without cover crop": true,
      "Other Citrus - with cover crop": true,
      "Other Citrus - without cover crop": true,
      "Tangerines - with cover crop": true,
      "Tangerines - without cover crop": true,
      "Cole Crops (Broccoli/Cauliflower)": false,
      "Corn (field)": false,
      "Corn (sweet)": false,
      Sorghum: false,
      Sudangrass: false,
      Cotton: false,
      "Grapes (raisin) - with cover crop": true,
      "Grapes (raisin) - without cover crop": true,
      "Grapes (table) - with cover crop": true,
      "Grapes (table) - without cover crop": true,
      "Grapes (wine) - with cover crop": true,
      "Grapes (wine) - without cover crop": true,
      "Kiwi - with cover crop": true,
      "Kiwi - without cover crop": true,
      Lettuce: false,
      Cantaloupe: false,
      Cucumbers: false,
      "Honeydew melons": false,
      Pumpkins: false,
      Squash: false,
      Watermelon: false,
      "Figs - with cover crop": true,
      "Figs - without cover crop": true,
      "Miscellaneous Deciduous - with cover crop": true,
      "Miscellaneous Deciduous - without cover crop": true,
      "Persimmons - with cover crop": true,
      "Persimmons - without cover crop": true,
      Hemp: false,
      Barley: false,
      "Cover Crop": false,
      "Forage Mix": false,
      "Other Small Grains": false,
      Teff: false,
      Triticale: false,
      Ryegrass: true,
      "Miscellaneous Subtropical - with cover crop": true,
      "Miscellaneous Subtropical - without cover crop": true,
      "Miscellaneous Truck Crops": false,
      Pasture: true,
      "Olives - with cover crop": true,
      "Olives - without cover crop": true,
      Garlic: false,
      Onions: false,
      "Nectarines - with cover crop": true,
      "Nectarines - without cover crop": true,
      "Peaches - with cover crop": true,
      "Peaches - without cover crop": true,
      "Pears - with cover crop": true,
      "Pears - without cover crop": true,
      "Pecans - with cover crop": true,
      "Pecans - without cover crop": true,
      Peppers: false,
      "Pistachios - with cover crop": true,
      "Pistachios - without cover crop": true,
      "Plums - with cover crop": true,
      "Plums - without cover crop": true,
      "Pomegranates - with cover crop": true,
      "Pomegranates - without cover crop": true,
      Potatoes: false,
      "Prunes - with cover crop": true,
      "Prunes - without cover crop": true,
      Rice: false,
      Safflower: false,
      Strawberries: false,
      Sunflower: false,
      "Sweet Potatoes": false,
      Tomatoes: false,
      Turf: true,
      Fallow: false,
      "Walnuts - with cover crop": true,
      "Walnuts - without cover crop": true,
      Wheat: false,
      "Sugar Beets": false,
      Oats: false,
    };
  }

  /**
   * Reload entities catalog (and reset selections) when the manager
   * switches the "Claim As" target. If the manager has entities already
   * selected, confirm before discarding them.
   */
  private async handleClaimAsChange(newId: string, previousId: string) {
    const ownId = this.authService.getUserId();

    if (this.selectedEntities.length > 0) {
      this.isAwaitingClaimAsConfirm = true;
      let confirmed = false;
      try {
        confirmed = await this.confirmDiscardEntities();
      } finally {
        this.isAwaitingClaimAsConfirm = false;
      }
      if (!confirmed) {
        this.isRevertingClaimAs = true;
        this.selectedClaimAsUserId.set(previousId);
        return;
      }
    }

    this.selectedEntities = [];
    this.formData.entities = [];
    this.showEntityDropdown = false;
    this.showAddNewEntityForm = false;

    if (newId === ownId) {
      this.growerEntities = [];
      return;
    }
    this.loadGrowerEntities(newId);
  }

  /**
   * Mirror the backend's entity normalize/dedup ([fields.service.ts:2694-2706])
   * client-side so the user sees what'll be kept and what'll be dropped.
   * Returns the kept list and the dropped rows (with reasons).
   */
  private dedupeEntities(entities: FieldEntity[]): {
    kept: FieldEntity[];
    dropped: { entity: FieldEntity; reason: string }[];
  } {
    const seen = new Map<string, FieldEntity>();
    const kept: FieldEntity[] = [];
    const dropped: { entity: FieldEntity; reason: string }[] = [];
    for (const e of entities ?? []) {
      const name = (e?.name ?? "").trim();
      const email = (e?.email ?? "").trim().toLowerCase();
      if (!name && !email) {
        // Fully empty row — skip silently, not worth surfacing
        continue;
      }
      if (!name) {
        dropped.push({ entity: { name, email }, reason: "missing name" });
        continue;
      }
      if (!email) {
        dropped.push({ entity: { name, email }, reason: "missing email" });
        continue;
      }
      const existing = seen.get(email);
      if (existing) {
        dropped.push({
          entity: { name, email },
          reason: `duplicate of ${existing.name}`,
        });
        continue;
      }
      const normalized = { name, email };
      seen.set(email, normalized);
      kept.push(normalized);
    }
    return { kept, dropped };
  }

  /**
   * If the entity list contains duplicates or invalid rows, show a confirm
   * dialog listing what will be dropped. Returns the kept list on confirm,
   * or null if the user cancels.
   */
  private async confirmDedupedEntities(
    entities: FieldEntity[],
  ): Promise<FieldEntity[] | null> {
    const { kept, dropped } = this.dedupeEntities(entities);
    if (dropped.length === 0) {
      return kept;
    }
    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const lines = dropped
      .map(({ entity, reason }) => {
        const name = escapeHtml(entity.name || "(no name)");
        const email = escapeHtml(entity.email || "(no email)");
        return `&bull; ${name} &lt;${email}&gt; — ${escapeHtml(reason)}`;
      })
      .join("<br>");
    const keptLine =
      kept.length === 0 ?
        "No entities will be saved."
      : `Only the remaining ${kept.length} ${kept.length === 1 ? "entity" : "entities"} will be saved.`;
    try {
      const result = await this.modalService.openDialogAsync({
        title: `${dropped.length} entry${dropped.length === 1 ? "" : "ies"} will be skipped`,
        message: `${lines}<br><br>${keptLine}`,
        variant: "info",
        actions: [
          { label: "Cancel", action: () => "cancel" as const },
          { label: "Continue", action: () => "confirm" as const },
        ],
      });
      return result === "confirm" ? kept : null;
    } catch {
      return null;
    }
  }

  private async confirmDiscardEntities(): Promise<boolean> {
    try {
      const result = await this.modalService.openDialogAsync({
        title: "Discard selected entities?",
        message:
          "Switching the grower will clear the entities you've already selected. Continue?",
        variant: "info",
        actions: [
          { label: "Cancel", action: () => "cancel" as const },
          { label: "Continue", action: () => "confirm" as const },
        ],
      });
      return result === "confirm";
    } catch {
      return false;
    }
  }

  formData: FieldFormData = {
    field_name: "",
    entities: [],

    crop_type_01: "Alfalfa",
    crop_type_liq_legend_01: null,
    ls_start_01: null,
    ls_end_01: null,
    irrigation_methods_01: ["Drip"],
    crop_type_02: null,
    crop_type_liq_legend_02: null,
    ls_start_02: null,
    ls_end_02: null,
    irrigation_methods_02: null,
    crop_type_03: null,
    crop_type_liq_legend_03: null,
    ls_start_03: null,
    ls_end_03: null,
    irrigation_methods_03: null,
    total_target_water: 0,
    total_target_type: "ET",
    total_target_water_unit: "Feet",
    is_deficit_irrigation: null,
    isperennialtree: false,
  };

  readonly deficitIrrigationOptions = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  /**
   * Check if all parameter are null or has null-like meaning
   */
  isAllNull(
    crop_type: any,
    irrigation_method: any,
    start_date: any,
    end_date: any,
  ): boolean {
    return (
      (!crop_type || crop_type === "None") &&
      (!irrigation_method ||
        irrigation_method === "None" ||
        !irrigation_method[0] ||
        irrigation_method[0] === "None") &&
      !start_date &&
      !end_date
    );
  }

  isAllNotNull(
    crop_type: any,
    irrigation_method: any,
    start_date: any,
    end_date: any,
  ): boolean {
    return (
      // Check if crop_type is truthy and not equal to 'None'
      crop_type &&
      crop_type !== "None" &&
      // Check if irrigation_method is an array with one valid element
      Array.isArray(irrigation_method) &&
      irrigation_method.length === 1 &&
      irrigation_method[0] &&
      irrigation_method[0] !== "None" &&
      // Check if start_date is a valid non-empty string that is a valid date
      start_date &&
      start_date !== "" &&
      !isNaN(new Date(start_date).getTime()) &&
      // Check if end_date is a valid non-empty string that is a valid date
      end_date &&
      end_date !== "" &&
      !isNaN(new Date(end_date).getTime())
    );
  }

  isValidEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Load entity catalog for the grower whose fields are being edited.
   * Pass `targetUserId` when a manager is acting on behalf of a grower.
   */
  loadGrowerEntities(targetUserId?: string) {
    this.dataService.getGrowerEntities(targetUserId).subscribe({
      next: (entities) => {
        this.growerEntities = entities;
      },
      error: (err) => {
        console.error("Failed to load grower entities", err);
        this.growerEntities = [];
      },
    });
  }

  /** Toggle the entity multi-select dropdown */
  toggleEntityDropdown() {
    this.showEntityDropdown = !this.showEntityDropdown;
  }

  /** Close entity dropdown (e.g., when clicking outside) */
  closeEntityDropdown() {
    setTimeout(() => {
      this.showEntityDropdown = false;
    }, 200);
  }

  /** Check if an entity is currently selected */
  isEntitySelected(entry: FieldEntity): boolean {
    return this.selectedEntities.some((e) => e.email === entry.email);
  }

  /** Toggle selection of a single entity */
  toggleEntitySelection(entry: FieldEntity) {
    const index = this.selectedEntities.findIndex(
      (e) => e.email === entry.email,
    );
    if (index >= 0) {
      this.selectedEntities = this.selectedEntities.filter(
        (_, i) => i !== index,
      );
    } else {
      this.selectedEntities = [...this.selectedEntities, entry];
    }
    this.syncFormDataFromSelectedEntities();
    this.isFormValid = this.checkFormValidity();
  }

  /** Toggle select all / deselect all */
  toggleSelectAll() {
    if (this.selectedEntities.length === this.growerEntities.length) {
      this.selectedEntities = [];
    } else {
      this.selectedEntities = [...this.growerEntities];
    }
    this.syncFormDataFromSelectedEntities();
    this.isFormValid = this.checkFormValidity();
  }

  /** Show/hide the add new entity inline form */
  toggleAddNewEntityForm() {
    this.showAddNewEntityForm = !this.showAddNewEntityForm;
    // Close the dropdown when opening the add form
    if (this.showAddNewEntityForm) {
      this.showEntityDropdown = false;
    }
    if (!this.showAddNewEntityForm) {
      this.newEntityName = "";
      this.newEntityEmail = "";
      this.newEntityValidationMsg = "";
    }
  }

  /** Add a new entity to the in-memory list and auto-select it */
  async addNewEntity() {
    const name = this.newEntityName.trim();
    const email = this.newEntityEmail.trim().toLowerCase();

    if (!name || !email) {
      this.newEntityValidationMsg =
        "Please fill in both entity name and email.";
      return;
    }
    if (!this.isValidEmail(email)) {
      this.newEntityValidationMsg = "Please enter a valid email address.";
      return;
    }
    // Check for duplicate email in the current entity catalog.
    if (this.growerEntities.some((e) => e.email.toLowerCase() === email)) {
      this.newEntityValidationMsg = "An entity with this email already exists.";
      return;
    }

    // Pre-flight: ask the backend if this email is allowed as an entity
    // (rejected if it belongs to an existing grower or manager). Surfaces
    // the error inline before the user fills the rest of the form and
    // hits SUBMIT. The backend still re-validates at field save as a
    // safety net for races / non-UI clients.
    this.isCheckingEntityEmail = true;
    this.newEntityValidationMsg = "";
    try {
      const result = await firstValueFrom(
        this.dataService.checkEntityEmail(email),
      );
      if (!result.valid) {
        this.newEntityValidationMsg =
          result.reason || "This email can't be added as an entity.";
        return;
      }
    } catch {
      this.newEntityValidationMsg =
        "Couldn't verify the email. Please try again.";
      return;
    } finally {
      this.isCheckingEntityEmail = false;
    }

    const newEntry: FieldEntity = { name, email };
    this.growerEntities = [...this.growerEntities, newEntry];
    this.selectedEntities = [...this.selectedEntities, newEntry];
    this.syncFormDataFromSelectedEntities();

    // Reset form
    this.showAddNewEntityForm = false;
    this.newEntityName = "";
    this.newEntityEmail = "";
    this.newEntityValidationMsg = "";
    this.isFormValid = this.checkFormValidity();
  }

  /** Sync formData.entities from selectedEntities */
  private syncFormDataFromSelectedEntities() {
    this.formData.entities = [...this.selectedEntities];
  }

  checkFormValidity() {
    if (!this.formData?.field_name) {
      this.validationInfo = "Please fill the Field Name";
      return false;
    }
    // Allow 0 for Total Target Water if the only crop is Fallow for the full year
    const allowZeroWater = this.isFallow;
    // ET Inches 0 - 60
    if (
      this.formData?.total_target_type === "ET" &&
      this.formData?.total_target_water_unit === "Inches" &&
      (this.formData?.total_target_water > 60 ||
        (allowZeroWater ?
          this.formData?.total_target_water < 0
        : this.formData?.total_target_water <= 0))
    ) {
      this.validationInfo =
        "For ET, Please set the water between 0 to 60 Inches";
      return false;
    }
    // Applied Water Inches 0 - 60
    if (
      this.formData?.total_target_type === "Applied Water" &&
      this.formData?.total_target_water_unit === "Inches" &&
      (this.formData?.total_target_water > 60 ||
        (allowZeroWater ?
          this.formData?.total_target_water < 0
        : this.formData?.total_target_water <= 0))
    ) {
      this.validationInfo =
        "For Applied Water, Please set the water between 0 to 60 Inches";
      return false;
    }
    // ET Feet 0 - 5
    if (
      this.formData?.total_target_type === "ET" &&
      this.formData?.total_target_water_unit === "Feet" &&
      (this.formData?.total_target_water > 5 ||
        (allowZeroWater ?
          this.formData?.total_target_water < 0
        : this.formData?.total_target_water <= 0))
    ) {
      this.validationInfo = "For ET, Please set the water between 0 to 5 Feet";
      return false;
    }
    // Applied Water Feet 0 - 5
    if (
      this.formData?.total_target_type === "Applied Water" &&
      this.formData?.total_target_water_unit === "Feet" &&
      (this.formData?.total_target_water > 5 ||
        (allowZeroWater ?
          this.formData?.total_target_water < 0
        : this.formData?.total_target_water <= 0))
    ) {
      this.validationInfo =
        "For Applied Water, Please set the water between 0 to 5 Feet";
      return false;
    }
    // Check the first crop group, Crop1 and Irri1 are both not none, so mainly check the start_date1 and end_date1
    if (
      !this.formData?.ls_start_01 ||
      !this.formData?.ls_end_01 ||
      moment(this.formData?.ls_start_01, "YYYY-MM-DD").isSameOrAfter(
        moment(this.formData?.ls_end_01, "YYYY-MM-DD"),
      )
    ) {
      this.validationInfo =
        "Please Set the Valid Date Range for Crop 1 (End is After Start)";
      return false;
    }

    // **Check whether skipped, second group all null but third group all not null
    if (
      this.isAllNull(
        this.formData?.crop_type_02,
        this.formData?.irrigation_methods_02,
        this.formData?.ls_start_02,
        this.formData?.ls_end_02,
      ) &&
      this.isAllNotNull(
        this.formData?.crop_type_03,
        this.formData?.irrigation_methods_03,
        this.formData?.ls_start_03,
        this.formData?.ls_end_03,
      )
    ) {
      this.validationInfo =
        "Please not Skip the crop group, fill the crop group one by one.";
      return false;
    }

    // Check the second group, should be all valid or all null
    if (
      // Not All null or Not All Not Null
      !(
        this.isAllNull(
          this.formData?.crop_type_02,
          this.formData?.irrigation_methods_02,
          this.formData?.ls_start_02,
          this.formData?.ls_end_02,
        ) ||
        this.isAllNotNull(
          this.formData?.crop_type_02,
          this.formData?.irrigation_methods_02,
          this.formData?.ls_start_02,
          this.formData?.ls_end_02,
        )
      )
    ) {
      this.validationInfo =
        "Please Fill All Valid values for Second Crop Group or Keep all Empty";
      return false;
    }

    // Check dates of second group
    if (
      this.isAllNotNull(
        this.formData?.crop_type_02,
        this.formData?.irrigation_methods_02,
        this.formData?.ls_start_02,
        this.formData?.ls_end_02,
      ) &&
      (moment(this.formData?.ls_start_02, "YYYY-MM-DD").isSameOrBefore(
        moment(this.formData?.ls_end_01, "YYYY-MM-DD"),
      ) ||
        moment(this.formData?.ls_start_02, "YYYY-MM-DD").isSameOrAfter(
          moment(this.formData?.ls_end_02, "YYYY-MM-DD"),
        ))
    ) {
      this.validationInfo =
        "Please Set the Valid Date Range for Crop 2 (End is After Start), and should not overlap the first date range";
      return false;
    }

    // Check the third group, should be all valid or all null
    if (
      // Not All null or Not All Not Null
      !(
        this.isAllNull(
          this.formData?.crop_type_03,
          this.formData?.irrigation_methods_03,
          this.formData?.ls_start_03,
          this.formData?.ls_end_03,
        ) ||
        this.isAllNotNull(
          this.formData?.crop_type_03,
          this.formData?.irrigation_methods_03,
          this.formData?.ls_start_03,
          this.formData?.ls_end_03,
        )
      )
    ) {
      this.validationInfo =
        "Please Fill All Valid values for Third Crop Group or Keep all Empty";
      return false;
    }

    // Check dates of third group
    if (
      this.isAllNotNull(
        this.formData?.crop_type_03,
        this.formData?.irrigation_methods_03,
        this.formData?.ls_start_03,
        this.formData?.ls_end_03,
      ) &&
      (moment(this.formData?.ls_start_03, "YYYY-MM-DD").isSameOrBefore(
        moment(this.formData?.ls_end_02, "YYYY-MM-DD"),
      ) ||
        moment(this.formData?.ls_start_03, "YYYY-MM-DD").isSameOrAfter(
          moment(this.formData?.ls_end_03, "YYYY-MM-DD"),
        ))
    ) {
      this.validationInfo =
        "Please Set the Valid Date Range for Crop 3 (End is After Start), and should not overlap the second date range";
      return false;
    }

    if (this.requiresPreclaimedAcknowledgement()) {
      this.validationInfo = "Please confirm the pre-claimed information.";
      return false;
    }

    this.validationInfo = "";
    return true;
  }

  requiresPreclaimedAcknowledgement(): boolean {
    return (
      !this.isManager &&
      !this.owner &&
      this.hasPreclaimedInfoForClaim &&
      !this.preclaimedInfoAcknowledged
    );
  }

  updatePreclaimedInfoAcknowledgement(event: Event): void {
    this.preclaimedInfoAcknowledged = (
      event.target as HTMLInputElement
    ).checked;
    this.isFormValid = this.checkFormValidity();
  }

  // Deprecated disclaimer gate: handled upstream in map component before opening this modal
  private ensureClaimDisclaimerAccepted(): boolean {
    return true;
  }

  /**
   * Use to pick the pre-selected crops when the dropdown was first load
   */
  readonly selectedCrops = (cropName: string) =>
    this.crops?.find((crop) => crop.crop_type === cropName)?.id ?? null;

  updateFormValue(key: string, event: { target: { value: unknown } } | Event) {
    const formValue = (event as any).target.value;
    if (key.slice(0, 4) === "irri") {
      //
      this.formData[key] = [formValue];
    } else if (key.slice(0, 4) === "crop") {
      // Get the crop object
      const crop = this.crops?.find((crop) => {
        return crop.crop_type === formValue;
      });
      //
      // Assign value to the specific group
      if (key === "crop_type_01") {
        this.formData["crop_type_01"] = crop?.crop_type ?? "Alfalfa";
        this.formData["crop_type_liq_legend_01"] = crop?.liq_legend ?? null;
        if (this.cropIsPerennial[crop!.crop_type]) {
          // Set up the default water year date for start 1 and end 1
          this.formData.ls_start_01 = startDateWaterYear().format("YYYY-MM-DD");
          this.formData.ls_end_01 = endDateWaterYear().format("YYYY-MM-DD");
        } else {
          this.formData.ls_start_01 = null;
          this.formData.ls_end_01 = null;
        }
        // Adding operation based on Fallow field
        const wasFallow = this.isFallow;
        this.isFallow = this.formData["crop_type_01"] === "Fallow";
        if (this.isFallow) {
          this.formData.ls_start_01 = startDateWaterYear().format("YYYY-MM-DD");
          this.formData.ls_end_01 = endDateWaterYear().format("YYYY-MM-DD");
          this.formData.crop_type_02 = null;
          this.formData.crop_type_liq_legend_02 = null;
          this.formData.ls_start_02 = null;
          this.formData.ls_end_02 = null;
          this.formData.irrigation_methods_02 = null;
          this.formData.crop_type_03 = null;
          this.formData.crop_type_liq_legend_03 = null;
          this.formData.ls_start_03 = null;
          this.formData.ls_end_03 = null;
          this.formData.irrigation_methods_03 = null;
          this.firstCropIrrigationMethodSelectedOption.set("Non-irrigated");
        } else if (wasFallow) {
          this.firstCropIrrigationMethodSelectedOption.set("Drip");
        }
      }
      if (key === "crop_type_02") {
        if (formValue !== "None") {
          this.formData["crop_type_02"] = crop?.crop_type ?? null;
          this.formData["crop_type_liq_legend_02"] = crop?.liq_legend ?? null;
        } else {
          this.formData["crop_type_02"] = "None";
        }
      }
      if (key === "crop_type_03") {
        if (formValue !== "None") {
          this.formData["crop_type_03"] = crop?.crop_type ?? null;
          this.formData["crop_type_liq_legend_03"] = crop?.liq_legend ?? null;
        } else {
          this.formData["crop_type_03"] = "None";
        }
      }
    } else if (key === "total_target_water") {
      //
      this.formData[key] = Number(formValue);
    } else {
      //
      this.formData[key] = formValue;
    }

    //
    this.isFormValid = this.checkFormValidity();
  }

  processCreateOrUpdateDto(submitDTO: FieldFormData) {
    /**
     * Assign (7):
     * 1. field_name
     * 2. total_target_water
     * 3. total_target_type
     * 4. total_target_water_unit
     * 5. crop_type_01
     * 6. crop_type_liq_legend_01
     * 7. irrigation_methods_01
     * 8. entity
     */
    submitDTO.field_name = this.formData.field_name;
    submitDTO.entities = [...this.formData.entities];

    submitDTO.total_target_water = this.formData.total_target_water;
    submitDTO.total_target_type = this.formData.total_target_type;
    submitDTO.total_target_water_unit = this.formData.total_target_water_unit;
    submitDTO.is_deficit_irrigation = this.formData.is_deficit_irrigation;
    submitDTO.crop_type_01 = this.formData.crop_type_01;
    submitDTO.crop_type_liq_legend_01 = this.cropPairs[submitDTO.crop_type_01];
    submitDTO.irrigation_methods_01 = this.formData.irrigation_methods_01;

    /**
     * Process (6):
     * 1. crop_type_02
     * 2. crop_type_liq_legend_02
     * 3. irrigation_methods_02
     * 4. crop_type_03
     * 5. crop_type_liq_legend_03
     * 6. irrigation_methods_03
     */
    // Crop 2, Crop Liq 2, Irrigation 2
    submitDTO.crop_type_02 =
      this.formData.crop_type_02 && this.formData.crop_type_02 !== "None" ?
        this.formData.crop_type_02
      : null;
    submitDTO.crop_type_liq_legend_02 =
      submitDTO.crop_type_02 ? this.cropPairs[submitDTO.crop_type_02] : null;
    submitDTO.irrigation_methods_02 =
      (
        this.formData.irrigation_methods_02 &&
        this.formData.irrigation_methods_02[0] !== "None"
      ) ?
        this.formData.irrigation_methods_02
      : null;
    // Crop 3, Crop Liq 3, Irrigation 3
    submitDTO.crop_type_03 =
      this.formData.crop_type_03 && this.formData.crop_type_03 !== "None" ?
        this.formData.crop_type_03
      : null;
    submitDTO.crop_type_liq_legend_03 =
      submitDTO.crop_type_03 ? this.cropPairs[submitDTO.crop_type_03] : null;
    submitDTO.irrigation_methods_03 =
      (
        this.formData.irrigation_methods_03 &&
        this.formData.irrigation_methods_03[0] !== "None"
      ) ?
        this.formData.irrigation_methods_03
      : null;

    /**
     * Assign (6):
     * 1. ls_start_01
     * 2. ls_end_01
     * 3. ls_start_02
     * 4. ls_end_02
     * 5. ls_start_03
     * 6. ls_end_03
     */
    submitDTO.ls_start_01 = this.formData.ls_start_01 ?? null;
    submitDTO.ls_end_01 = this.formData.ls_end_01 ?? null;
    submitDTO.ls_start_02 =
      this.formData.ls_start_02 ? this.formData.ls_start_02 : null;
    submitDTO.ls_end_02 =
      this.formData.ls_end_02 ? this.formData.ls_end_02 : null;
    submitDTO.ls_start_03 =
      this.formData.ls_start_03 ? this.formData.ls_start_03 : null;
    submitDTO.ls_end_03 =
      this.formData.ls_end_03 ? this.formData.ls_end_03 : null;

    // Set isperennialtree (1)
    const crop1 = this.crops?.find((crop) => {
      return crop.crop_type === submitDTO.crop_type_01;
    });
    // * If only 1 crop exists and its a tree, then the field is perennial
    //
    if (crop1?.istree && !submitDTO.crop_type_02 && !submitDTO.crop_type_03) {
      submitDTO.isperennialtree = true;
    }
    //
  }

  /**
   *  Submit the form to the server
   */
  async onSubmitCropRecords() {
    if (!this.ensureClaimDisclaimerAccepted()) {
      return;
    }

    const submitDTO: FieldFormData = {
      field_name: "",
      entities: [],

      crop_type_01: "",
      crop_type_liq_legend_01: null,
      ls_start_01: null,
      ls_end_01: null,
      irrigation_methods_01: [],
      crop_type_02: null,
      crop_type_liq_legend_02: null,
      ls_start_02: null,
      ls_end_02: null,
      irrigation_methods_02: null,
      crop_type_03: null,
      crop_type_liq_legend_03: null,
      ls_start_03: null,
      ls_end_03: null,
      irrigation_methods_03: null,
      total_target_water: 0,
      total_target_type: "ET",
      total_target_water_unit: "Feet",
      is_deficit_irrigation: null,
      isperennialtree: false,
    };

    this.processCreateOrUpdateDto(submitDTO);

    // Mirror the backend dedup so the user sees what'll be dropped (duplicate
    // emails, missing name/email) and can confirm or cancel before submit.
    const decided = await this.confirmDedupedEntities(submitDTO.entities ?? []);
    if (decided === null) {
      return;
    }
    submitDTO.entities = decided;

    // Start the loading state
    if (this.owner) {
      this.loadingService.start({
        token: "field-updating",
        message: "Updating",
      });
    } else {
      this.loadingService.start({
        token: "field-updating",
        message: "Preparing field data",
      });
    }

    // ? Not using finalize here to ensure loading stop only after async polling
    const claimAsUserId =
      this.selectedClaimAsUserId() === this.authService.getUserId() ?
        undefined
      : this.selectedClaimAsUserId();
    this.dataService
      .createOrUpdateFields(this.id, submitDTO, claimAsUserId)
      .subscribe({
        next: async () => {
          this.closeModal();
          try {
            await this.pollData(2500, 20);
            // IM-613: emit the edited field id so a listener can patch just that
            // field in place (grower) instead of re-fetching the whole collection.
            // Listeners that ignore the arg (e.g. manager) keep their old behavior.
            this.fieldOperatedEvent.emit(this.id);
          } catch (error) {
            this.snackbarService.show({
              message:
                "Error: Failed to claim or update the field, please let Land IQ Team :)",
              duration: 5000,
            });
          }
          // Turn off the loading state
          this.loadingService.stop("field-updating");
          this.snackbarService.show({
            message: "Success! Field has been updated",
            duration: 3000,
          });
        },
        error: (error: any) => {
          // The interceptor unwraps inline-only errors (returns error.error
          // directly) and suppresses the global toast for them. So for the
          // entity-email case, `state` and `message` live at the top level.
          if (error?.state === "not_valid_entity_email") {
            this.validationInfo =
              error?.message ||
              "This email belongs to an existing grower or manager and can't be added as an entity.";
            this.isFormValid = false;
          } else {
            console.error("Failed to Create Or Update Crop Records", error);
            this.snackbarService.show({
              message: "Something went wrong. Please try again.",
              duration: 5000,
            });
          }
          this.loadingService.stop("field-updating");
        },
      });
  }

  async onDeleteCropRecords() {
    const fieldName = this.formData?.field_name || `Field ${this.id}`;

    await deleteFieldUtil(
      this.id,
      fieldName,
      "Delete",
      {
        dataService: this.dataService,
        loadingService: this.loadingService,
        modalService: this.modalService,
        snackbarService: this.snackbarService,
      },
      () => {
        this.closeModal();
        // IM-613: emit the field id; after a delete/unclaim the patch fetch
        // returns the field with owner=false so the map shows it as available.
        this.fieldOperatedEvent.emit(this.id);
      },
    );
  }

  userField: any;

  private loadManagerClaimedDetailsForField(fieldId: number): void {
    const requestSeq = ++this.preclaimedRequestSeq;
    this.hasPreclaimedInfoForClaim = false;
    this.preclaimedInfoAcknowledged = false;

    this.dataService.getPreclaimedInfo(fieldId).subscribe({
      next: (response) => {
        if (
          requestSeq !== this.preclaimedRequestSeq ||
          this.id !== fieldId ||
          this.owner
        ) {
          return;
        }

        if (response.hasPreclaimedInfo && response.data) {
          this.hasPreclaimedInfoForClaim = true;
          this.applyManagerClaimedDetails(response.data);
        }
      },
      error: (error) => {
        console.error("Failed to load manager preclaimed details:", error);
      },
    });
  }

  private applyManagerClaimedDetails(details: Partial<FieldFormData>): void {
    const { field_name, ...prefillDetails } = details;
    Object.assign(this.formData, prefillDetails);
    this.formData.entities = [];
    this.selectedEntities = [];
    this.firstCropIrrigationMethodSelectedOption.set(
      this.formData.irrigation_methods_01?.[0] ?? "Drip",
    );
    this.isFallow = this.formData.crop_type_01 === "Fallow";
    this.isFormValid = this.checkFormValidity();
  }

  // ngOnInit Hook
  ngOnInit() {
    this.modalSubscription =
      this.modalService.fieldSelectUpdateModal$.subscribe((visible) => {
        this.visible = visible;
      });
    // Load existing entities for the grower dropdown (only for GROWER role)
    if (!this.isManager) {
      this.loadGrowerEntities();
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.isInitialized) {
      this.isInitialized = true;
    } else {
      // Refresh the grower's entity catalog every time the modal reopens
      // so removed/added entities (from this or other fields) are reflected.
      // Manager case is handled either by the signal effect (when they pick
      // a grower) or in the owner-true branch below.
      if (!this.isManager) {
        this.loadGrowerEntities();
      }
      // Reset the form
      this.hasPreclaimedInfoForClaim = false;
      this.preclaimedInfoAcknowledged = false;
      this.formData = {
        field_name: "",
        entities: [],
        crop_type_01: "Alfalfa",
        crop_type_liq_legend_01: null,
        ls_start_01: null,
        ls_end_01: null,
        irrigation_methods_01: ["Drip"],
        crop_type_02: null,
        crop_type_liq_legend_02: null,
        ls_start_02: null,
        ls_end_02: null,
        irrigation_methods_02: null,
        crop_type_03: null,
        crop_type_liq_legend_03: null,
        ls_start_03: null,
        ls_end_03: null,
        irrigation_methods_03: null,
        total_target_water: 0,
        total_target_type: "ET",
        total_target_water_unit: "Feet",
        is_deficit_irrigation: null,
        isperennialtree: false,
      };
      if (this.owner && this.id) {
        const userField = await firstValueFrom(
          this.dataService.getFieldById(this.id.toString()),
        );
        this.userField = userField?.[0];
        // Assign the value to the formData
        this.formData.field_name = this.userField.field_name;
        // Pre-select entities in the multi-select from the existing field
        const existingEntities: FieldEntity[] =
          Array.isArray(this.userField.entities) ?
            this.userField.entities.filter(
              (e: FieldEntity) => e && e.email && e.name,
            )
          : [];
        this.selectedEntities = [...existingEntities];
        this.formData.entities = [...existingEntities];
        // Manager opening a grower's field: align the Claim-As dropdown with
        // the actual owner and fetch that grower's entity catalog. Sync
        // lastClaimAsUserId first so the effect short-circuits and doesn't
        // prompt the discard confirmation on this programmatic change.
        if (this.isManager && this.userField?.user_id) {
          const fieldOwnerId = this.userField.user_id as string;
          const ownId = this.authService.getUserId();
          if (fieldOwnerId !== ownId) {
            this.lastClaimAsUserId = fieldOwnerId;
            this.selectedClaimAsUserId.set(fieldOwnerId);
            this.loadGrowerEntities(fieldOwnerId);
          } else {
            // Manager owns this field — make sure the dropdown reflects
            // that, not a grower carried over from a previous session.
            this.lastClaimAsUserId = ownId;
            this.selectedClaimAsUserId.set(ownId);
            this.growerEntities = [];
          }
        }
        this.formData.total_target_water = this.userField.total_target_water;
        this.formData.total_target_type = this.userField.total_target_type;
        this.formData.total_target_water_unit =
          this.userField.total_target_water_unit;
        this.formData.is_deficit_irrigation =
          this.userField.is_deficit_irrigation ?? null;
        this.formData.crop_type_01 = this.userField.crop_type_01;
        this.formData.irrigation_methods_01 =
          this.userField.irrigation_methods_01;
        this.firstCropIrrigationMethodSelectedOption.set(
          this.userField.irrigation_methods_01?.[0] ?? "Drip",
        );
        this.formData.ls_start_01 = this.userField.ls_start_01;
        this.formData.ls_end_01 = this.userField.ls_end_01;
        // Can be null value
        this.formData.crop_type_02 =
          this.userField.crop_type_02 ? this.userField.crop_type_02 : null;
        this.formData.irrigation_methods_02 =
          this.userField.irrigation_methods_02 ?
            this.userField.irrigation_methods_02
          : null;
        this.formData.ls_start_02 =
          this.userField.ls_start_02 ? this.userField.ls_start_02 : null;
        this.formData.ls_end_02 =
          this.userField.ls_end_02 ? this.userField.ls_end_02 : null;
        this.formData.crop_type_03 =
          this.userField.crop_type_03 ? this.userField.crop_type_03 : null;
        this.formData.irrigation_methods_03 =
          this.userField.irrigation_methods_03 ?
            this.userField.irrigation_methods_03
          : null;
        this.formData.ls_start_03 =
          this.userField.ls_start_03 ? this.userField.ls_start_03 : null;
        this.formData.ls_end_03 =
          this.userField.ls_end_03 ? this.userField.ls_end_03 : null;
      } else {
        this.userField = null;
        this.selectedEntities = [];
        this.firstCropIrrigationMethodSelectedOption.set("Drip");
        if (!this.isManager && this.id) {
          this.loadManagerClaimedDetailsForField(this.id);
        }
        // Manager opening a fresh (unclaimed) field: reset Claim-As to
        // themselves. Without this the dropdown would carry over from the
        // previous modal session, risking accidental claims for the wrong
        // grower. Sync lastClaimAsUserId first so the effect short-circuits.
        if (this.isManager) {
          const ownId = this.authService.getUserId();
          this.lastClaimAsUserId = ownId;
          this.selectedClaimAsUserId.set(ownId);
          this.growerEntities = [];
        }
      }
      this.isFallow = this.formData.crop_type_01 === "Fallow";
    }
  }

  // ngOnDestroy Hook, unsubscribe the modalSubscription when the component is destroyed.
  ngOnDestroy() {
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }
    this.loadingService.stop("field-updating");
  }
}
